import * as React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Stack,
  Autocomplete,
  Typography,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import { RichTextEditor } from './components/RichTextEditor';
import { sanitizeTelegramHtml } from './utils';

interface City {
  citiesId: number;
  name?: string;
}

interface Settings {
  welcomeMessage: string;
  cityIdBiggerPrice: string[];
  adminLoginName: string;
  adminPassword: string;
  adminName: string;
  isMacrosOn: boolean;
  shareNumberBtnTitle: string;
  sendManagerMessages: boolean;
  syncProductImages: boolean;
}

const API_URL = import.meta.env.VITE_API_URL;

const Settings = () => {
  const [settings, setSettings] = React.useState<Settings>({
    welcomeMessage: '',
    cityIdBiggerPrice: [],
    adminLoginName: '',
    adminPassword: '',
    adminName: '',
    isMacrosOn: false,
    sendManagerMessages: true,
    shareNumberBtnTitle: '',
    syncProductImages: true,
  });

  const [cities, setCities] = React.useState<City[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saved, setSaved] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const [googleSyncLoading, setGoogleSyncLoading] = React.useState(false);
  const [shopsSyncLoading, setShopsSyncLoading] = React.useState(false);
  const [deleteOldProducts, setDeleteOldProducts] = React.useState(true);
  const [deleteOldShops, setDeleteOldShops] = React.useState(true);
  const [deleteManagers, setDeleteManagers] = React.useState(false);
  const [productsResult, setProductsResult] = React.useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const [shopsResult, setShopsResult] = React.useState<{
    ok: boolean;
    message: string;
  } | null>(null);

  React.useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/settings`).then((r) => r.json()),
      fetch(`${API_URL}/cities`).then((r) => r.json()),
    ]).then(([settingsRes, citiesRes]) => {
      const settingsData = settingsRes?.settings || {};

      setSettings({
        welcomeMessage: settingsData.welcomeMessage || '',
        cityIdBiggerPrice: Array.isArray(settingsData?.cityIdBiggerPrice)
          ? settingsData.cityIdBiggerPrice.map(String)
          : JSON.parse(settingsData?.cityIdBiggerPrice || '[]').map(String),
        adminLoginName: settingsData.adminLoginName || '',
        adminPassword: '',
        adminName: settingsData.adminName || '',
        isMacrosOn: settingsData.isMacrosOn ?? false,
        sendManagerMessages: settingsData.sendManagerMessages ?? true,
        shareNumberBtnTitle: settingsData.shareNumberBtnTitle || '',
        syncProductImages: settingsData.syncProductImages ?? true,
      });

      setCities(Array.isArray(citiesRes?.cities) ? citiesRes.cities : []);
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    await fetch(`${API_URL}/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...settings,
        welcomeMessage: sanitizeTelegramHtml(settings.welcomeMessage || ''),
      }),
    });

    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const downloadBackup = async () => {
    const res = await fetch(`${API_URL}/db/export`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup_${Date.now()}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const uploadCSV = async () => {
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const res = await fetch(`${API_URL}/db/import`, {
      method: 'POST',
      body: formData,
    });

    if (res.ok) {
      alert('CSV завантажено успішно');
      setFiles([]);
    } else {
      const data = await res.json();
      alert('Помилка при імпорті: ' + data.error);
    }
  };

  const uploadGoogleSheets = async () => {
    try {
      setGoogleSyncLoading(true);
      setProductsResult(null);

      const res = await fetch(
        `${API_URL}/db/sync?deleteOld=${deleteOldProducts}`,
        { method: 'GET' },
      );
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setProductsResult({ ok: true, message: data.message || 'Готово' });
        setFiles([]);
      } else {
        setProductsResult({
          ok: false,
          message: data.error || 'Помилка при імпорті',
        });
      }
    } catch (err) {
      setProductsResult({ ok: false, message: 'Помилка при імпорті' });
    } finally {
      setGoogleSyncLoading(false);
    }
  };

  const uploadGoogleSheetsShops = async () => {
    if (
      deleteManagers &&
      !window.confirm(
        'Видалити всіх менеджерів (крім менеджера підтримки)? Цю дію не можна відмінити.',
      )
    ) {
      return;
    }

    try {
      setShopsSyncLoading(true);
      setShopsResult(null);

      const res = await fetch(
        `${API_URL}/db/sync-shops?deleteOld=${deleteOldShops}&deleteManagers=${deleteManagers}`,
        { method: 'GET' },
      );
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setShopsResult({ ok: true, message: data.message || 'Готово' });
      } else {
        setShopsResult({
          ok: false,
          message: data.error || 'Помилка при імпорті',
        });
      }
    } catch (err) {
      setShopsResult({ ok: false, message: 'Помилка при імпорті' });
    } finally {
      setShopsSyncLoading(false);
    }
  };

  if (loading) return null;

  return (
    <Card sx={{ maxWidth: 800 }}>
      <CardHeader title="Налаштування" />
      <CardContent>
        <Stack spacing={3}>
          <RichTextEditor
            value={settings.welcomeMessage}
            onChange={(html) =>
              setSettings((prev) => ({
                ...prev,
                welcomeMessage: html,
              }))
            }
            label="Привітальне повідомлення при реєстрації"
            minRows={4}
          />

          <TextField
            fullWidth
            size="small"
            label='Напис на кнопці "Поділитись номером"'
            value={settings.shareNumberBtnTitle || ''}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                shareNumberBtnTitle: e.target.value,
              }))
            }
          />

          <Autocomplete
            multiple
            options={cities}
            getOptionLabel={(option) => option.name || String(option.citiesId)}
            value={cities.filter((c) =>
              settings.cityIdBiggerPrice.includes(String(c.citiesId)),
            )}
            onChange={(_, value) =>
              setSettings((prev) => ({
                ...prev,
                cityIdBiggerPrice: value.map((v) => String(v.citiesId)),
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Міста з підвищеною ціною" />
            )}
          />

          <TextField
            label="Логін до адмін панелі"
            value={settings.adminLoginName}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                adminLoginName: e.target.value,
              }))
            }
            fullWidth
          />

          <TextField
            label="Пароль до адмін панелі"
            type="password"
            value={settings.adminPassword}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                adminPassword: e.target.value,
              }))
            }
            fullWidth
          />

          <TextField
            label="Ім'я адміністратора панелі"
            value={settings.adminName}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                adminName: e.target.value,
              }))
            }
            fullWidth
          />

          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: 1,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography variant="h6">
              Додаткові налаштування логіки системи
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.isMacrosOn}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      isMacrosOn: e.target.checked,
                    }))
                  }
                />
              }
              label="Увімкнути макроси (${name}, ${price} і т.д.)"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.sendManagerMessages}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      sendManagerMessages: e.target.checked,
                    }))
                  }
                />
              }
              label="Відправка повідомлень менеджеру при замовленні"
            />

            <Typography variant="caption" color="text.secondary">
              При включенні: повідомлення про замовлення користувача буде
              надсилатися менеджеру. При виключенні — менеджер не отримує ці
              повідомлення.
            </Typography>
          </Box>

          <Button variant="contained" onClick={handleSave}>
            Зберегти
          </Button>

          {saved && <Typography color="success.main">Все збережено</Typography>}

          <Box sx={{ display: 'flex', gap: 4, mt: 3 }}>
            <Box sx={{ flex: 1, p: 3, borderRadius: 2, boxShadow: 1 }}>
              <Typography variant="h6">Скачати бекап</Typography>
              <Typography variant="body2" color="text.secondary">
                Ви можете завантажити дані з бази даних.
              </Typography>
              <Button variant="outlined" onClick={downloadBackup}>
                Скачати бекап
              </Button>
            </Box>

            <Box sx={{ flex: 1, p: 3, borderRadius: 2, boxShadow: 1 }}>
              <Typography variant="h6">Завантажити CSV</Typography>
              <Typography variant="body2" color="text.secondary">
                Імпорт CSV у базу даних.
              </Typography>

              <Stack direction="row" spacing={2}>
                <input
                  type="file"
                  accept=".csv"
                  multiple
                  id="csv-upload"
                  style={{ display: 'none' }}
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                />

                <label htmlFor="csv-upload">
                  <Button variant="outlined" component="span">
                    {files.length ? `${files.length} файлів` : 'Вибрати CSV'}
                  </Button>
                </label>

                <Button
                  variant="contained"
                  onClick={uploadCSV}
                  disabled={!files.length}
                >
                  Завантажити
                </Button>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="h6">
              Імпорт з Google Sheets Продукції
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Повна заміна товарів у базі.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.syncProductImages}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      syncProductImages: e.target.checked,
                    }))
                  }
                />
              }
              label="Синхронізувати зображення продукції"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteOldProducts}
                  onChange={(e) => setDeleteOldProducts(e.target.checked)}
                />
              }
              label="Видалити старі продукти"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Button
                variant="outlined"
                onClick={uploadGoogleSheets}
                disabled={googleSyncLoading}
              >
                {googleSyncLoading ? <CircularProgress size={20} /> : 'Імпорт'}
              </Button>
              <Link
                href="https://docs.google.com/spreadsheets/d/19ltc7jOEKHZ4dXyPAoFah2SDIDqmB6aPYIhu2w6RyPY/edit?gid=69572911#gid=69572911"
                target="_blank"
                rel="noopener noreferrer"
              >
                Посилання на Google Sheets
              </Link>
            </Box>

            {productsResult && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  color: '#fff',
                  bgcolor: productsResult.ok ? 'success.main' : 'error.main',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {productsResult.message}
              </Box>
            )}
          </Box>

          <Box sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="h6">
              Імпорт з Google Sheets Магазини
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Повна заміна магазинів у базі.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteOldShops}
                  onChange={(e) => setDeleteOldShops(e.target.checked)}
                />
              }
              label="Видалити старі магазини"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteManagers}
                  onChange={(e) => setDeleteManagers(e.target.checked)}
                />
              }
              label="Видалити всіх менеджерів"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Button
                variant="outlined"
                onClick={uploadGoogleSheetsShops}
                disabled={shopsSyncLoading}
              >
                {shopsSyncLoading ? <CircularProgress size={20} /> : 'Імпорт'}
              </Button>
              <Link
                href="https://docs.google.com/spreadsheets/d/1QTvCqCf-219KwTAUnrQpZpGOY7FhL8AHCsMnbT5SUoQ/edit?gid=0#gid=0"
                target="_blank"
                rel="noopener noreferrer"
              >
                Посилання на Google Sheets
              </Link>
            </Box>

            {shopsResult && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  color: '#fff',
                  bgcolor: shopsResult.ok ? 'success.main' : 'error.main',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {shopsResult.message}
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default Settings;
